import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { TokenService } from '../token/token.service';

export enum HttpMethod {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  PATCH = 'PATCH',
  DELETE = 'DELETE',
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  constructor(private tokenService: TokenService) { }

  public async call<T = null>(method: HttpMethod, url_route: string, data?: any): Promise<T> {

    // Skip unauthenticated endpoints to avoid infinite loop
    const noauth = ['refresh', 'login', 'logout', 'signup'];
    if (!noauth.some(endpoint => url_route.includes(endpoint))) {
      await this.tokenService.ensureValidToken();
    }

    const token = this.tokenService.getToken();
    // Don't add a guard to make sure there's a token. Let the API handle it.

    const full_path = `${environment.api_base_url}/${url_route}`;
    console.log(`Calling route: ${full_path}`);
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };

    const body = data ? JSON.stringify(data) : undefined;
    console.log(`Request body: ${body}`);
    const response = await fetch(full_path, { method, headers, body });

    if (!response.ok) {
      // Surface server-provided error message (e.g. 403 AI usage cap text)
      // by parsing the JSON body when present. Falls back to statusText.
      const payload = await response.json().catch(() => null);
      const rawMessage = payload?.message ?? response.statusText ?? `API call failed (${response.status})`;
      const message = typeof rawMessage === 'string' ? rawMessage : JSON.stringify(rawMessage);
      throw Object.assign(
        new Error(message),
        { status: response.status, error: payload },
      );
    }

    // Check if the response has content before trying to parse it
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json') && response.status !== 204) {
      try {
        return await response.json() as T;
      } catch (error) {
        console.error('Failed to parse response JSON:', error);
        return {} as T;
      }
    } else {
      // For empty responses or non-JSON responses (like logout endpoint)
      console.log('Empty or non-JSON response received');
      return {} as T;
    }
  }

  /**
   * Makes an API call without authentication
   * @param method HTTP method (GET, POST, PUT, DELETE)
   * @param url_route API endpoint
   * @param data Request body data (optional)
   * @returns Promise with response data
   */
  public async callNoauth<T = null>(method: HttpMethod, url_route: string, data?: any): Promise<T> {
    const full_path = `${environment.api_base_url}/${url_route}`;
    console.log(`Calling route: ${full_path}`);
    const headers = {
      'Content-Type': 'application/json'
    };

    const body = data ? JSON.stringify(data) : undefined;
    console.log(`Request body: ${body}`);
    const response = await fetch(full_path, { method, headers, body });

    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      throw Object.assign(
        new Error(payload?.message ?? response.statusText),
        { status: response.status, error: payload }
      );
    }

    // Check if the response has content before trying to parse it
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json') && response.status !== 204) {
      try {
        return await response.json() as T;
      } catch (error) {
        console.error('Failed to parse response JSON:', error);
        return {} as T;
      }
    } else {
      // For empty responses or non-JSON responses (like logout endpoint)
      console.log('Empty or non-JSON response received');
      return {} as T;
    }
  }



  public async uploadFile<T = null>(url_route: string, formData: FormData): Promise<T> {

    // Skip token check for refresh endpoint to avoid infinite loop
    if (url_route !== 'auth/refresh' && url_route !== 'auth/logout') {
      await this.tokenService.ensureValidToken();
    }

    const token = this.tokenService.getToken();
    if (!token) {
      throw new Error('No token found');
    }

    const full_path = `${environment.api_base_url}/${url_route}`;
    const headers = {
      'Authorization': `Bearer ${token}`
    };

    const response = await fetch(full_path, {
      method: HttpMethod.POST,
      headers,
      body: formData
    });

    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      const rawMessage = payload?.message ?? response.statusText ?? `API call failed (${response.status})`;
      const message = typeof rawMessage === 'string' ? rawMessage : JSON.stringify(rawMessage);
      throw Object.assign(
        new Error(message),
        { status: response.status, error: payload },
      );
    }

    return response.json();
  }
}
